import { type SelectOwnerState } from '@mui/base'
import { clsx } from 'clsx'
import { type ComponentProps, type JSX, type MouseEvent, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import { IconPlus } from '@redteclab/icons'

import { Button } from 'base-ui'

import { useProductSearchRefinementContext } from '../ProductSearchRefinement'
import { ProductSearchRefinementClearRefinement } from '../ProductSearchRefinementClearRefinement'
import { useProductSearchRefinementStateContext } from '../ProductSearchRefinementStateContext'

import { ProductSearchRefinementTextInput } from './ProductSearchRefinementTextInput'

const MINIMUM_ITEMS_FOR_SEARCH = 10

type ProductSearchRefinementCheckboxListboxBodyProps<
  OptionValue extends NonNullable<unknown>,
  Multiple extends boolean,
> = ComponentProps<'div'> & {
  ownerState: SelectOwnerState<OptionValue, Multiple> & {
    onClose: (event: MouseEvent<HTMLButtonElement>) => void
  }
}

export const ProductSearchRefinementCheckboxListboxBody = <
  OptionValue extends NonNullable<unknown>,
  Multiple extends boolean,
>(
  props: ProductSearchRefinementCheckboxListboxBodyProps<OptionValue, Multiple>,
): JSX.Element => {
  const { children, className, ownerState, ...restProps } = props

  const { onClose } = ownerState

  const {
    state: { isMobile },
  } = useProductSearchRefinementStateContext()

  const refinementContext = useProductSearchRefinementContext()

  if (!('refinementList' in refinementContext)) {
    throw new Error('The refinement is not a list refinement.')
  }

  const { refinementList, refinementListOptions } = refinementContext

  const { attribute } = refinementListOptions

  const isSearchable =
    !isMobile &&
    (refinementList.isFromSearch ||
      refinementList.items.length >= MINIMUM_ITEMS_FOR_SEARCH)

  const isSelected = useMemo(
    () => refinementList.items.some((item) => item.isRefined),
    [refinementList],
  )

  const showMore =
    isMobile &&
    refinementList.canToggleShowMore &&
    !refinementList.isShowingMore

  return (
    <div
      className={clsx(
        className,
        'justify-between self-stretch bg-light-primary-medium desktop:bg-transparent',
      )}
      {...restProps}
    >
      {isSearchable ? (
        <ProductSearchRefinementTextInput
          attribute={attribute}
          className="hidden desktop:mb-4 desktop:me-4 desktop:ms-6 desktop:mt-3 desktop:block"
          searchForItems={refinementList.searchForItems}
        />
      ) : null}

      <div className="flex-1 overflow-y-auto">
        {children}
        {showMore ? (
          <Button
            className="pl-6"
            data-qa-id={`form-expandable-link-${attribute}-expand`}
            onClick={() => {
              refinementList.toggleShowMore()
            }}
            size="medium"
            startIcon={<IconPlus />}
            variant="text"
          >
            <FormattedMessage id="general.label.showMore" />
          </Button>
        ) : null}
      </div>

      <div className="p-6 desktop:hidden">
        <Button
          className="w-full whitespace-pre-wrap"
          data-qa-id={
            isSelected
              ? `form-refinement-list-confirm-selection-${attribute}`
              : `form-refinement-list-close-madal-${attribute}`
          }
          onClick={onClose}
          type="button"
        >
          {isSelected ? (
            <FormattedMessage id="general.label.link.apply" />
          ) : (
            <FormattedMessage id="general.link.label.back" />
          )}
        </Button>
      </div>

      <ProductSearchRefinementClearRefinement
        className="hidden self-start desktop:mb-4 desktop:me-4 desktop:ms-6 desktop:mt-6 desktop:inline-flex"
        includedAttributes={[attribute]}
      />
    </div>
  )
}
